import { CompositeNavigationProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { GeocomplyPayload } from '@/data/location/types';
import { BetPosition, BetStatus, EventStatus } from '@/feature/bets-sbk/hooks/types';
import {
    EventDetails,
    MarketType,
    OptionStatus,
    OptionType,
    Player,
    Sport,
    Supplier,
    Team,
} from '@/feature/event-details-sbk/types';
import { UserSettings } from '@/hooks/use-auth-user-settings';
import { Product } from '@/hooks/use-jurisdiction';
import { SocialCurrency } from '@/hooks/use-social-currency';
import { BetSlipStackParamList, RootStackParamList } from '@/navigation/types';
import { PromotionType } from '@/types/api.generated';
import { MatchUpdateMessage, OddsUpdateMessageOption } from '@/utils/websocket/types';
import { z } from 'zod';

export const MAX_SELECTIONS = 20;

/**
 * Promo metadata used to validate and apply a promo to a bet.
 * Returned by GET /auth/user/promotions and consumed in place of BonusEnginePromo.
 */
export type SbkPromoMetadata = {
    PROFIT_BONUS_PERCENT?: number;
    MAX_STAKE?: number;
    MAX_PAYOUT_AMOUNT?: number;
    TARGET_AMOUNT?: number;
};

export type SbkPromoCriterion = {
    type: string;
    domain: string;
    parameters?: Record<string, string | number>;
};

export type SbkPromo = {
    campaign_token: string;
    promo_slug: string | null;
    title: string;
    description: string;
    status: string;
    type: PromotionType;
    metadata: SbkPromoMetadata;
    criteria?: SbkPromoCriterion[] | null;
    progress_amount?: number;
};

export const BetTypes = {
    Single: 'SINGLE',
    Combo: 'COMBO',
} as const;

export type BetType = (typeof BetTypes)[keyof typeof BetTypes];

export const TotalStakeErrors = {
    ExceedDailyWagerLimit: 'EXCEED_DAILY_WAGER_LIMIT',
    ExceedMonthlyWagerLimit: 'EXCEED_MONTHLY_WAGER_LIMIT',
    ExceedWeeklyWagerLimit: 'EXCEED_WEEKLY_WAGER_LIMIT',
    ExceedWalletCashBalance: 'EXCEED_WALLET_CASH_BALANCE',
    ExceedWalletBetrBucksBalance: 'EXCEED_BETR_BUCKS_BALANCE',
    ExceedWalletCashBetrBucksBalance: 'EXCEED_WALLET_CASH_BETR_BUCKS_BALANCE',
} as const;

export type TotalStakeError = (typeof TotalStakeErrors)[keyof typeof TotalStakeErrors];

export const StakeInputErrors = {
    BelowMin: 'BELOW_MIN',
    AboveMax: 'ABOVE_MAX',
    ExceedMaxSingleWager: 'EXCEED_MAX_SINGLE_WAGER',
    ExceedSingleWalletBalance: 'EXCEED_SINGLE_WALLET_BALANCE',
    ExceedStakeLimit: 'EXCEED_STAKE_LIMIT',
    DecimalsNotAllowed: 'DECIMALS_NOT_ALLOWED',
} as const;

export type StakeInputError = (typeof StakeInputErrors)[keyof typeof StakeInputErrors];

export type Selection = {
    id: string;
    optionId: string;
    marketId: string;
    eventId: string;
    isComboEnabled: boolean;
};

export type Bet = {
    id: string;
    betType: BetType;
    stake?: number;
    displayStake?: string;
    stakeInputError?: StakeInputError;
};

export type BetSlipMarket = {
    id: string;
    description: string;
    eventId: string;
    marketType: MarketType;
    isMicroMarket: boolean;
    published: boolean;
    player?: Player;
    supplier?: Supplier;
};

export type BetSlipEvent = {
    id: string;
    start_time: number;
    home_team: Team;
    away_team: Team;
    is_sgp_enabled: boolean;
    sport: Sport;
    event_details: EventDetails;
    status: EventStatus;
};

export type BetSlipOption = {
    id: string;
    odds: number;
    originalOdds: number; // this is the odds value when user pick up the selection
    description: string;
    marketId: string;
    optionType: OptionType;
    status: OptionStatus;
};

export type ClosedSelection = {
    option: BetSlipOption;
    market: BetSlipMarket;
    event: BetSlipEvent;
    bet: Bet;
};

export type SelectionParam = {
    option: BetSlipOption;
    market: BetSlipMarket;
    event: BetSlipEvent;
};

// carried from share-link import until placement so the conversion can be attributed
export type SbkBetslipReferrer = {
    linkId: string;
    entryId: string;
    // option ids that landed from the share; conversion only counts bets containing them
    optionIds: string[];
};

export type ProducerStatus = 'UP' | 'DOWN';

export type BetSubmittedState = {
    isCoinsWager: boolean;
    selections: Record<string, Selection>;
    events: Record<string, BetSlipEvent>;
    markets: Record<string, BetSlipMarket>;
    options: Record<string, BetSlipOption>;
    isBetrBucksEnabled: Record<string, boolean>;
    selectedPromos: Record<string, SbkPromo | null>;
    singlesBets: Bet[];
    comboBet?: { odds: number; selections: Selection[]; bet: Bet };
    submittedBets: Record<string, SubmittedBet>;
};

export type SubmittedBet = {
    status: BetStatus;
    globalId: string;
    localId: string;
    position?: BetPosition | null;
};

export type ConfirmedBetMapping = Record<string, { globalId: string; position?: BetPosition | null }>;

export type UpdateOrFetchNewSgpOddsOptions = {
    filterEventId?: string;
    forceUpdate?: boolean;
    showComboOddsLoading?: boolean;
};

export type SbkBetSlip = {
    product: Product.Sweeps | Product.Prediction | null;
    selections: Record<string, Selection>;
    events: Record<string, BetSlipEvent>;
    markets: Record<string, BetSlipMarket>;
    options: Record<string, BetSlipOption>;
    bets: Record<string, Bet>;
    isBetrBucksEnabled: Record<string, boolean>; // for each bet id, whether it is using betr bucks (bonus) or not
    sgpOdds: Record<string, number | false>; // for scripts / combo+ bets "option1id-option2id-option3id" => odds | false
    selectionOrder: string[];
    eventOrder: string[];
    editBetId: string | null;
    showKeyboard: boolean;
    totalStakeErrors: TotalStakeError[];
    betSubmissionStatus: BetSubmissionStatus;
    submittedBets: Record<string, SubmittedBet>;
    submittedState: BetSubmittedState;
    closedSelections: Array<ClosedSelection>;
    oddsChanges: Record<string, number>;
    sgpOddsChanges: Record<string, number>;
    sgpEventDisabled: Record<string, boolean>;
    sgpTemporaryIssue: Record<string, boolean>;
    userSettings: UserSettings | null;
    oddsChangeTimeout: ReturnType<typeof setTimeout> | null;
    producerStatuses: Partial<Record<Supplier, ProducerStatus>>;
    isSgpFetching: boolean;
    showComboOddsLoading: boolean;
    betSubmissionStartTime: number | null;
    submittedCurrency: SocialCurrency | null;
    selectedPromos: Record<string, SbkPromo | null>;
    isAutoPromoDismissed: boolean;
    referrer?: SbkBetslipReferrer;
    actions: {
        addSelection: (option: BetSlipOption, market: BetSlipMarket, event: BetSlipEvent) => Promise<void>;
        addComboFeaturedBet: (selections: SelectionParam[]) => Promise<void>;
        addSharedBetSelections: (
            selections: SelectionParam[],
            referrer: Omit<SbkBetslipReferrer, 'optionIds'>
        ) => Promise<void>;
        addSgpFeaturedBet: (selections: SelectionParam[], sgpOdds: Record<string, number>) => Promise<void>;
        addSgpPlusFeaturedBet: (selections: SelectionParam[], sgpOdds: Record<string, number>) => Promise<void>;
        removeSelection: (selectionId: string) => Promise<void>;
        removeFeaturedBet: (selectionIds: string[]) => Promise<void>;
        clearOddsChanges: () => void;
        clearHigherOddsChanges: () => void;
        acceptAllOddsChanges: () => void;
        toggleComboSelectionStatus: (selectionId: string) => void;
        toggleMultipleSelectionStatus: (selectionIds: string[]) => void;
        updateStake: (betId: string, stake: number, displayStake: string, betType: BetType) => void;
        clearBetStake: (betId: string) => void;
        updateUserSettings: (settings: UserSettings) => void;
        clearBetSlip: () => void;
        clearReferrer: () => void;
        resetOnLogout: () => void;
        keepSelections: () => void;
        keepUnstakedSelections: () => void;
        placeBets: (userId: string, onFail: (error: unknown) => void) => Promise<void>;
        setEditingBet: (betId: string | null) => void;
        toggleUseBetrBucks: (betId: string) => void;
        updateStakeInputErrors: (betId: string, error?: StakeInputError) => void;
        updateTotalStakeErrors: (errors?: TotalStakeError[]) => void;
        handleOddsUpdateMessages: (oddsUpdate: OddsUpdateMessageOption[]) => void;
        refreshSelectionOdds: () => Promise<void>;
        handleMatchUpdateMessage: (matchUpdate: MatchUpdateMessage) => void;
        updateSubmittedBetStatus: (
            betId: string,
            status: BetStatus,
            globalBetId: string,
            rejectionReason: string,
            onComplete: (outcome: BetSubmissionOutcome) => void,
            position?: BetPosition | null
        ) => void;
        clearClosedSelections: () => void;
        removeClosedSelections: () => void;
        updateProducerStatus: (supplier: Supplier, status: ProducerStatus) => void;
        pollSubmittedBets: (onPollFinish: (outcome: BetSubmissionOutcome) => void) => Promise<void>;
        updateBetSubmissionStatus: (betSubmissionStatus: 'SUCCESS' | 'ERROR') => void;
        updateAllEventDetails: () => Promise<void>;
        updateOrFetchNewSgpOdds: (options?: UpdateOrFetchNewSgpOddsOptions) => Promise<void>;
        handleWsSgpOddsUpdate: (eventId: string) => void;
        setSelectedPromo: (betId: string, promo: SbkPromo | null) => void;
        removeSelectedPromo: (betId: string) => void;
        autoApplyPromos: (promosByBetId: Record<string, SbkPromo>) => void;
        enableBetrBucksAndRemovePromo: (betId: string) => void;
        clearBetrBucks: () => void;
    };
};

export const BetSubmissionStatus = {
    Idle: 'IDLE',
    Submitting: 'SUBMITTING',
    Success: 'SUCCESS',
    Error: 'ERROR',
} as const;

export type BetSubmissionStatus = (typeof BetSubmissionStatus)[keyof typeof BetSubmissionStatus];

export type BetSlipTab = 'Parlays' | 'Singles';

export type BetSlipParamsList = {
    [K in BetSlipTab]: undefined;
};

export type BetSubmissionSummary = {
    totalCount: number;
    wageredAmount: number;
};

export type BetSubmissionOutcome =
    | { status: 'confirmed'; summary: BetSubmissionSummary }
    | { status: 'rejected' }
    | { status: 'unconfirmed' };

export class GeoComplyError extends Error {
    payload: GeocomplyPayload;

    constructor(payload: GeocomplyPayload) {
        super('Geocomply error');
        this.payload = payload;
    }
}

type BetErrorParams = {
    amount?: string;
    // TODO: add additional fields here for dynamic translation depending on the error code above
};

export type BetPlacementErrorCodes =
    | 'generic_fallback'
    | 'market_limit_exceeded'
    | 'custom_odds_are_different_from_db'
    | 'huddle_service_odds_are_different_than_requested'
    | 'custom_unable_to_find_market_or_betting_on_blocked_content'
    | 'current_odds_are_different_than_requested'
    | 'current_odds_are_lower_than_requested'
    | 'polymarket_opposing_position_already_held'
    | 'bet_exceeds_maximum_site_liability_limit'; // add all error codes here

export class SubmitBetError extends Error {
    code?: BetPlacementErrorCodes;
    params?: BetErrorParams;

    constructor(message: string, code: BetPlacementErrorCodes | undefined, params: BetErrorParams) {
        super(message);
        this.code = code;
        this.params = params;
    }
}

export class GenerateBetsPayloadError extends Error {
    code: string;
    msg: string;

    constructor(code: string) {
        super('Generate bets payload error');
        this.msg = 'Generate bets payload error';
        this.code = code;
    }
}

export type SgpPayload = {
    event_id: string;
    bets: Array<{
        side_bet_id: string;
        side_bet_option_id: string;
    }>;
};

export type GetSgpOddsResponse = {
    data: {
        odds: number;
    };
};

export const ComboBetTypes = {
    Parlay: 'PARLAY',
    SGP: 'SGP',
    SGP_Plus: 'SGP+',
} as const;

export type ComboBetType = (typeof ComboBetTypes)[keyof typeof ComboBetTypes];

export const sgpTemporaryErrorCodeSchema = z.enum([
    'sgp_temporarily_unavailable',
    'custom_market_is_not_open',
    'custom_unable_to_find_market',
    'custom_market_is_suspended',
    'custom_market_does_not_have_options',
    'custom_option_does_not_belong_to_market',
    'custom_option_does_not_have_bet_value',
    'custom_selection_is_suspended',
    'custom_selection_is_not_open',
    //Huddle API returned error
    'sgp_market_is_not_open',
    'sgp_market_not_found',
    'sgp_custom_selection_is_suspended',
    'sgp_not_available_pricing',
    'sgp_matrix_not_available',
    'sgp_unknown_error_code',
]);

export const sgpDisabledErrorCodeSchema = z.enum([
    'sgp_not_enabled',
    'sgp_service_error',
    'sgp_not_allowed_for_event',
    'sgp_event_not_found',
]);

export const sgpConflictingErrorCodeSchema = z.enum([
    'conflicting_selections',
    'custom_markets_are_duplicated',
    'custom_markets_option_are_duplicated',
]);

const sgpErrorCodeSchema = sgpTemporaryErrorCodeSchema.or(sgpDisabledErrorCodeSchema).or(sgpConflictingErrorCodeSchema);

export type SgpBetErrorCodes = z.infer<typeof sgpErrorCodeSchema>;

export class SgpBetError extends Error {
    code: SgpBetErrorCodes;

    constructor(message: string, code: SgpBetErrorCodes) {
        super(message);
        this.code = code;
    }
}

export class AddSelectionConflictingError extends Error {}
export class AddSelectionMaxSelectionsError extends Error {}

export type BetSuccessNavigationProp = CompositeNavigationProp<
    NativeStackNavigationProp<BetSlipStackParamList, 'BetSuccessScreen'>,
    NativeStackNavigationProp<RootStackParamList>
>;
